import {
  PrivacyNoticeBlockKind,
  type PrivacyNotice,
  type PrivacyNoticeBlock,
  type PrivacyNoticeTextRun
} from '../types/PrivacyNotice'
import type { User } from '../types/User'

export const PRIVACY_NOTICE_CONTACT_EMAIL = 'erik@intraction.com'

const EMPHASIS_MARKER = '**'

const paragraph = (text: string): PrivacyNoticeBlock => ({ kind: PrivacyNoticeBlockKind.PARAGRAPH, text })

const subheading = (text: string): PrivacyNoticeBlock => ({ kind: PrivacyNoticeBlockKind.SUBHEADING, text })

const bulletList = (...items: string[]): PrivacyNoticeBlock => ({ kind: PrivacyNoticeBlockKind.LIST, items })

const contactEmail: PrivacyNoticeBlock = { kind: PrivacyNoticeBlockKind.CONTACT, email: PRIVACY_NOTICE_CONTACT_EMAIL }

// Text between ** markers is emphasised: 'a **b** c' becomes 'a ', 'b' (emphasised), ' c'.
export const toPrivacyNoticeTextRuns = (text: string): PrivacyNoticeTextRun[] =>
  text
    .split(EMPHASIS_MARKER)
    .map((segment, index) => ({ text: segment, isEmphasized: index % 2 === 1 }))
    .filter((run) => run.text.length > 0)

// Acknowledged before versions were recorded; sorts before every real version
const UNVERSIONED_ACKNOWLEDGEMENT = ''

// Anyone who acknowledged an older version is asked again; raise it to PRIVACY_NOTICE.version when people must re-read
export const OLDEST_ACCEPTED_PRIVACY_NOTICE_VERSION = UNVERSIONED_ACKNOWLEDGEMENT

export const hasAcknowledgedPrivacyNotice = (
  user: Pick<User, 'privacyPolicyAgree' | 'privacyNoticeVersion'> | undefined,
  oldestAcceptedVersion: string = OLDEST_ACCEPTED_PRIVACY_NOTICE_VERSION
): boolean => {
  if (!user?.privacyPolicyAgree) return false
  const acknowledgedVersion = user.privacyNoticeVersion ?? UNVERSIONED_ACKNOWLEDGEMENT
  return acknowledgedVersion >= oldestAcceptedVersion
}

export const PRIVACY_NOTICE: PrivacyNotice = {
  version: '2026-09-29',
  lastUpdated: 'Last updated: 29 September 2026',
  introduction: [
    paragraph('We want you to understand what happens to your information when you use **Intraction**.'),
    paragraph(
      'Intraction is designed to provide you and/or your team with feedback from your participation in a training session or exercise. We use technical safeguards to separate your identity from the data used for analysis, and we limit how AI is used within the service.'
    )
  ],
  sections: [
    {
      title: 'The short version',
      blocks: [
        paragraph('When you use Intraction:'),
        bulletList(
          'We collect the information needed to create your account, run the session and provide your feedback.',
          '**Intraction is responsible for determining how and why your personal data is processed for the purposes described in this notice.**',
          'Your identity is separated from the data used for analysis using pseudonymous identifiers.',
          'The information that links those identifiers back to you is stored separately.',
          'Direct identifying information is removed before session data enters the analysis pipeline.',
          'Analytical classifications and scores are produced using predefined, deterministic methods rather than generative AI.',
          'AI may be used **after the analysis is complete** to turn those results into clear, readable feedback and report text.',
          'The AI does not receive the separate identity key and is not used to determine your underlying classifications or scores.',
          'Intraction does not make employment or other decisions about you through automated processing.',
          'Core service infrastructure and processing are located within the EU/EEA where this hosting option applies.',
          'A US-based email distribution provider may process your email address and delivery metadata where necessary to send service communications.'
        ),
        paragraph('More detail is provided below.')
      ]
    },
    {
      title: '1. Who is responsible for your data?',
      blocks: [
        paragraph('**Intraction is the data controller** for the processing described in this privacy notice.'),
        paragraph(
          'This means that Intraction determines the purposes for which your personal data is processed and the principal means by which that processing takes place.'
        ),
        paragraph(
          'Your organisation may provide access to Intraction, organise the training session, invite you to participate, and receive the aggregated outputs described in this notice. However, Intraction remains responsible for the processing for which it acts as controller.'
        ),
        paragraph(
          'Questions about our processing of your personal data or requests to exercise your data protection rights can be sent to:'
        ),
        contactEmail,
        paragraph(
          'If your organisation separately processes information received from Intraction for its own purposes, it may act as an independent data controller for that separate processing. Your organisation is responsible for informing you about such processing.'
        )
      ]
    },
    {
      title: '2. What information do we process?',
      blocks: [
        paragraph(
          'Depending on how the service is configured, we may process the following categories of information.'
        ),
        subheading('Account and identification information'),
        paragraph('This may include:'),
        bulletList(
          'your name;',
          'work email address;',
          'organisation;',
          'team or session membership; and',
          'an internal user or session identifier.'
        ),
        paragraph(
          'This information is used to provide access to the service, administer your participation and ensure that feedback is delivered to the correct participant.'
        ),
        subheading('Session and interaction information'),
        paragraph('The service records information generated through your participation in the training exercise.'),
        paragraph('This may include:'),
        bulletList(
          'actions and choices made during the exercise;',
          'task and session outcomes;',
          'timestamps and event data;',
          'interaction and communication-related measures;',
          'responses provided during the exercise;',
          'team-level measures; and',
          'analytical classifications or scores generated from the session.'
        ),
        paragraph(
          'Spoken communication is processed as part of the exercise. Audio is used to create transcripts and is then discarded. The transcripts are stripped of direct personal identifiers and pseudonymised before further processing.'
        ),
        paragraph(
          'Please do not share sensitive personal information or discuss sensitive personal topics while using Intraction unless this is specifically required and authorised as part of the exercise.'
        ),
        subheading('Technical information'),
        paragraph(
          'We may also process limited technical information necessary to operate and secure the service, such as:'
        ),
        bulletList(
          'login and authentication events;',
          'device or browser information;',
          'IP address;',
          'error and diagnostic information; and',
          'security logs.'
        )
      ]
    },
    {
      title: '3. How is your identity protected?',
      blocks: [
        paragraph(
          'We use **pseudonymisation** to reduce the amount of identifying information used during processing.'
        ),
        paragraph(
          'Your identifying information and the key that connects you to your session data are stored separately from the data used for analysis.'
        ),
        paragraph(
          'Before session information enters the analytical processing pipeline, direct identifiers such as your name and email address are removed.'
        ),
        paragraph(
          'The analytical data is therefore processed under a pseudonymous identifier rather than your direct identity.'
        ),
        paragraph(
          'Because a separate mapping key exists that can reconnect the information to you, we treat this information as **personal data under GDPR**, rather than claiming that it is anonymous.'
        ),
        paragraph(
          'The mapping is required so that Intraction can provide the correct feedback to the correct participant.'
        )
      ]
    },
    {
      title: '4. How is your session data analysed?',
      blocks: [
        paragraph('Intraction may automatically analyse aspects of participation in the training exercise.'),
        paragraph(
          'The underlying analysis and classification are performed using **predefined and deterministic processing methods**. In other words, the system applies defined analytical rules and calculations to relevant session information.'
        ),
        paragraph(
          'These methods may, for example, identify or quantify patterns relevant to the training exercise and generate the results used in your feedback.'
        ),
        paragraph(
          "Because this processing can evaluate aspects of an individual's activity, behaviour or performance, some of it may constitute **profiling** within the meaning of GDPR."
        ),
        paragraph(
          'We use this processing to provide learning and development feedback to participants and aggregated insight about the training exercise.'
        ),
        paragraph(
          'Intraction does **not** use this processing to make decisions concerning your employment, compensation, promotion, dismissal, disciplinary status, eligibility, access to services or other decisions having legal or similarly significant effects on you.'
        )
      ]
    },
    {
      title: '5. How do we use AI?',
      blocks: [
        paragraph('Generative AI has a limited role in Intraction.'),
        paragraph(
          'It is **not used to perform the underlying behavioural analysis, determine your classifications or calculate your analytical results.**'
        ),
        paragraph(
          'Instead, AI may be used after the analytical processing has already taken place to help convert structured analytical results into natural, readable text—for example, to produce explanations in an individual feedback report or written summaries of already established findings.'
        ),
        paragraph('Before information is provided to the AI component:'),
        bulletList(
          'direct personal identifiers are removed;',
          'the separate identity mapping key is not provided to the AI;',
          'the relevant analytical results have already been produced; and',
          'the AI receives only the information necessary to produce the requested text.'
        ),
        paragraph(
          'The AI component is intended to **communicate existing analytical results**, rather than independently determine how you should be assessed.'
        ),
        paragraph(
          'The resulting feedback is subsequently associated with the correct user within the controlled Intraction environment using the separately maintained identity mapping.'
        ),
        paragraph(
          'Personal data submitted through the service is not used to train third-party general-purpose AI models.'
        )
      ]
    },
    {
      title: '6. Do we make automated decisions about you?',
      blocks: [
        paragraph(
          'No decisions producing legal effects or similarly significant effects on you are made by Intraction solely through automated processing.'
        ),
        paragraph(
          'Automated analysis may be used to create training feedback, as described above, but Intraction does not determine matters such as:'
        ),
        bulletList(
          'whether you are hired or dismissed;',
          'your salary or compensation;',
          'promotion or disciplinary decisions;',
          'access to employment opportunities; or',
          'comparable decisions that significantly affect your rights or circumstances.'
        ),
        paragraph(
          'If your organisation separately uses information from Intraction to make decisions about you, that constitutes separate processing for which your organisation is responsible.'
        )
      ]
    },
    {
      title: '7. Why is your information processed?',
      blocks: [
        paragraph('Intraction processes your personal data for purposes including:'),
        bulletList(
          'creating and administering your account;',
          'providing access to the service;',
          'running the training exercise in which you participate;',
          'measuring and analysing session activity;',
          'calculating session and training results;',
          'producing individual feedback;',
          'producing aggregated team and organisation-level feedback;',
          'presenting analytical results in an understandable form;',
          'maintaining the security and reliability of the service;',
          'preventing misuse and protecting the integrity of the platform;',
          'troubleshooting technical problems; and',
          'sending necessary service communications.'
        ),
        paragraph(
          'We will not use your personal information for materially incompatible purposes without identifying an appropriate legal basis and providing additional information where required.'
        )
      ]
    },
    {
      title: '8. What is the legal basis for processing?',
      blocks: [
        paragraph(
          'Where **Intraction acts as the data controller**, we process personal data only where we have an appropriate legal basis under applicable data protection law.'
        ),
        paragraph('Depending on the particular processing activity and deployment, this may include:'),
        bulletList(
          'processing necessary for the performance of a contract or in order to take steps connected with a contract;',
          'processing based on our **legitimate interests** in providing, securing, evaluating and improving the Intraction service, where those interests are not overridden by your rights and interests; and',
          'processing necessary to comply with applicable legal obligations.'
        ),
        paragraph('Where we rely on legitimate interests, those interests may include:'),
        bulletList(
          'delivering the training and feedback functionality requested through the service;',
          'maintaining the security and reliability of Intraction;',
          'preventing misuse;',
          'supporting users and resolving technical issues; and',
          'understanding service performance in order to maintain and improve the product.'
        ),
        paragraph(
          'If we introduce processing that requires consent under applicable law, we will request that consent separately.'
        )
      ]
    },
    {
      title: '9. Who receives your information?',
      blocks: [
        paragraph(
          'Access to personal data is limited to people and service providers that need it for the purposes described in this notice.'
        ),
        paragraph('These may include:'),
        bulletList(
          'you, as the participant receiving your individual feedback;',
          'authorised personnel at Intraction;',
          'authorised facilitators or administrators at your organisation, within the access limits described below;',
          'infrastructure and hosting providers;',
          'technical service providers necessary to operate the platform; and',
          'an email distribution provider used to send service communications.'
        ),
        paragraph(
          'Our service providers process information on our behalf and are contractually restricted in how they may use it.'
        ),
        paragraph(
          '**Individual-level feedback is accessible only to you as an individual. Organisation facilitators can access aggregated feedback at team and organisation level.**'
        ),
        paragraph(
          'We do not provide your individual-level feedback to your organisation unless a different arrangement has been expressly communicated to you and has an appropriate legal basis.'
        )
      ]
    },
    {
      title: '10. Where is your information processed?',
      blocks: [
        paragraph('Intraction is operated from **Sweden**.'),
        paragraph(
          'Core infrastructure, analytical processing and service data can be hosted and processed within the **European Union / European Economic Area**, according to the applicable service configuration.'
        ),
        paragraph(
          'A separate email distribution provider based in the **United States** is used to deliver certain service communications.'
        ),
        paragraph('Where required for email delivery, this provider may receive limited personal information such as:'),
        bulletList(
          'your email address;',
          'message delivery information;',
          'timestamps; and',
          'related delivery metadata.'
        ),
        paragraph(
          'The email provider does **not** receive the session analysis data used to generate your training feedback.'
        ),
        paragraph(
          'For transfers of personal data outside the EEA, Intraction uses appropriate safeguards in accordance with applicable data protection law.'
        ),
        paragraph(
          'For the US-based email distribution provider, the applicable transfer safeguard is the **EU Standard Contractual Clauses (SCCs)**.'
        )
      ]
    },
    {
      title: '11. How long do we keep your information?',
      blocks: [
        paragraph(
          'Intraction retains personal data only for as long as necessary for the purposes described in this privacy notice.'
        ),
        paragraph('Retention periods may depend on the type of information and the applicable service arrangement.'),
        paragraph(
          "Where your access to Intraction is provided through an organisation, the end of that organisation's applicable service agreement may trigger deletion or de-identification of participant information."
        ),
        paragraph(
          'When the applicable retention period ends, the pseudonymisation layer connecting your analytical information with your identity is severed and directly identifying account information is deleted in accordance with our deletion procedures.'
        ),
        paragraph(
          "Once the mapping between you and remaining analytical data has been permanently destroyed, that information can no longer be associated with you through Intraction's systems."
        ),
        paragraph(
          'Certain limited information may be retained where required by law, necessary for the establishment or defence of legal claims, or contained temporarily in secure backups subject to defined deletion procedures.'
        ),
        paragraph(
          'We keep your personal data until you or your organisation asks us to delete it, unless it is deleted or de-identified earlier as described above.'
        )
      ]
    },
    {
      title: '12. How do we protect your information?',
      blocks: [
        paragraph(
          'We use technical and organisational safeguards designed to protect your information from unauthorised access, alteration, disclosure or loss.'
        ),
        paragraph('These measures include, as appropriate:'),
        bulletList(
          'separation of identity information from analytical data;',
          'pseudonymous identifiers;',
          'access controls;',
          'restricted access to re-identification information;',
          'encryption in transit and at rest where appropriate;',
          'configurable EU/EEA-based core infrastructure;',
          'logging and security monitoring;',
          'role-based permissions; and',
          'contractual and security requirements for service providers.'
        ),
        paragraph(
          'No system can eliminate all security risk, but we aim to limit both the amount of personal information processed and the number of systems and people able to associate analytical information with an identifiable individual.'
        )
      ]
    },
    {
      title: '13. Your data protection rights',
      blocks: [
        paragraph('Depending on the circumstances and applicable law, you may have the right to:'),
        bulletList(
          'request access to personal data held about you;',
          'request correction of inaccurate information;',
          'request deletion of your information;',
          'request restriction of processing;',
          'object to certain processing;',
          'receive certain information in a portable format; and',
          'obtain information about automated processing and profiling where applicable.'
        ),
        paragraph(
          'Some rights depend on the legal basis and circumstances of the processing and therefore do not apply in every situation.'
        ),
        paragraph(
          'Because **Intraction is the data controller for the processing described in this notice**, requests to exercise these rights can be submitted directly to:'
        ),
        contactEmail,
        paragraph('We may need to verify your identity before acting on a request.'),
        paragraph(
          "You also have the right to lodge a complaint with the competent data protection supervisory authority. For Intraction's establishment in Sweden, the relevant authority is the **Swedish Authority for Privacy Protection (Integritetsskyddsmyndigheten, IMY)**, without affecting your right to contact another competent supervisory authority where applicable."
        )
      ]
    },
    {
      title: '14. What happens if you choose not to provide information?',
      blocks: [
        paragraph('Some information is necessary for Intraction to function.'),
        paragraph(
          'For example, without the information required to identify your account and associate you with the relevant training session, we may not be able to provide individual feedback or allow you to participate in certain parts of the service.'
        ),
        paragraph('Where information is optional, this will be indicated where it is collected.'),
        paragraph(
          'If participation in an Intraction exercise is organised by your employer or another organisation, questions about whether participation itself is mandatory should be directed to that organisation.'
        )
      ]
    },
    {
      title: '15. Changes to this privacy notice',
      blocks: [
        paragraph(
          'We may update this notice when Intraction, its processing activities or applicable legal requirements change.'
        ),
        paragraph(
          'Where a change materially affects how your personal information is processed, we will provide appropriate notice before or when the change takes effect, as required by applicable law.'
        )
      ]
    },
    {
      title: 'Questions',
      blocks: [
        paragraph(
          'If you have questions about this privacy notice, how Intraction processes your personal information, or wish to exercise your data protection rights, please contact:'
        ),
        contactEmail
      ]
    }
  ]
}
